import React from "react";

import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";

import FormElement from "../../store/FormElement";
import { useEditing } from "../utils/formElement";
import {
  StyledDeleteIcon,
  StyledDragIndicatorIcon,
  StyledEditIcon,
  StyledHorizontalDivider,
  StyledInfoIcon,
  useStyles,
} from "../utils/Icons";

type VerticalSingleSelector = {
  label: string;
  options: {
    value: string;
    checked: boolean;
  }[];
};

interface Props {
  element: FormElement<VerticalSingleSelector>;
}

const FormVerticalSingleSelector: React.FC<Props> = observer(({ element }) => {
  const editing = useEditing(element);
  const { classes } = useStyles();

  function handleCheckOption(
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) {
    element.updateArgument("options", (options) => {
      if (options) {
        options[index].checked = event.target.checked;
      }
      return options;
    });
  }

  function handleEditValue(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) {
    element.updateArgument("options", (options) => {
      if (options) {
        options[index].value = event.target.value;
      }
      return options;
    });
  }

  function handleRemoveOption(index: number) {
    element.updateArgument("options", (options) => {
      if (options) {
        options.splice(index, 1);
      }
      return options;
    });
  }

  const handleAddOption = () => {
    element.updateArgument("options", (options) => {
      if (options) {
        options.push({ value: "", checked: false });
        return options;
      }
      return [{ value: "", checked: false }];
    });
  };

  return (
    <>
      {!editing.value ? (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon
            className={clsx(["styled-drag-indicator"])}
          />
          <div className={classes.longTextInputContent}>
            <Typography variant="h6" color="textPrimary">
              {element.arguments.label || "Vertical Single Selector label"}
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="choices-display"
                name="choices-display"
                value={element.arguments.options?.[0]?.value}
              >
                {element.arguments.options?.map((e, i) => (
                  <FormControlLabel
                    key={i}
                    value={e.value}
                    control={<Radio />}
                    label={e.value}
                    checked={e.checked}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </div>

          <div className={clsx([classes.editIcons, "edit-icons-container"])}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon onClick={() => element.remove()} />
          </div>
        </Box>
      ) : (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon />
          <Box className={classes.edit}>
            <Box className={classes.columnContent}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Vertical Single Selector
              </Typography>
              <TextField
                value={element.arguments.label}
                onChange={(e) => element.setArgument("label", e.target.value)}
              />
              <Typography variant="body2" color="textSecondary">
                Options
              </Typography>
              <Box
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                marginBottom={4}
              >
                {element.arguments.options?.map((e, i) => (
                  <TextField
                    style={{ margin: 8 }}
                    onChange={(e) => handleEditValue(e, i)}
                    value={e.value}
                    key={i}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          aria-label="delete"
                          onClick={() => handleRemoveOption(i)}
                          size="large"
                        >
                          <CloseIcon />
                        </IconButton>
                      ),
                      startAdornment: (
                        <Radio
                          checked={e.checked}
                          onChange={(e) => handleCheckOption(e, i)}
                          value={e.value}
                          inputProps={{ "aria-label": "A" }}
                        />
                      ),
                    }}
                  />
                ))}
              </Box>
              <div style={{ width: "30%" }}>
                <Button
                  variant="text"
                  startIcon={<AddIcon />}
                  onClick={handleAddOption}
                >
                  Add item
                </Button>
              </div>
              <StyledHorizontalDivider orientation="horizontal" />
              <div className={classes.editFooter}>
                <div className={classes.editButtons}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        aria-label="Required"
                        checked={element.required}
                        onChange={(e) => element.setRequired(e.target.checked)}
                        inputProps={{ "aria-label": "primary checkbox" }}
                      />
                    }
                    label="Required"
                  />
                  <Button variant="outlined" onClick={editing.cancel}>
                    Cancel
                  </Button>
                  <Button
                    className={classes.saveChangesButton}
                    onClick={editing.stop}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </Box>
          </Box>
          <div className={classes.editIcons}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon />
          </div>
        </Box>
      )}
    </>
  );
});

export default FormVerticalSingleSelector;
